import React, { useState } from 'react';
import { Clock, User, Plus, Minus, ExternalLink, ChevronDown, Wrench } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent } from '../../ui/dialog';
import { LineItem } from '../../../models/workItem';
import propertyService from '../../../services/propertyService';
import { cn } from "../../../lib/utils";

interface InspectionQuoteLineItemDetailsProps {
  lineItem: LineItem;
  propertyId: string;
}

const InspectionQuoteLineItemDetails: React.FC<InspectionQuoteLineItemDetailsProps> = ({ lineItem, propertyId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);
  const [carouselImages, setCarouselImages] = useState<{ url: string; description: string }[]>([]);
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleMaterials = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMaterials((prev) => !prev);
  };

  const calculateSubTaskCosts = () => {
    return lineItem.analysis.subTasks.reduce(
      (acc, subTask) => {
        const laborCosts = subTask.options.reduce(
          (optAcc, option) => ({
            costLow: optAcc.costLow + option.expectedLaborHoursLow * option.laborCostLowPerHour,
            costHigh: optAcc.costHigh + option.expectedLaborHoursHigh * option.laborCostHighPerHour,
            rateLow: Math.min(optAcc.rateLow || Infinity, option.laborCostLowPerHour),
            rateHigh: Math.max(optAcc.rateHigh || 0, option.laborCostHighPerHour),
          }),
          {
            costLow: 0,
            costHigh: 0,
            rateLow: Infinity,
            rateHigh: 0,
          }
        );

        const materialCosts = subTask.options.reduce(
          (optAcc, option) => ({
            costLow:
              optAcc.costLow +
              option.materials.reduce(
                (matAcc, material) => matAcc + material.lowCostPerUnit * material.quantityLow,
                0
              ),
            costHigh:
              optAcc.costHigh +
              option.materials.reduce(
                (matAcc, material) => matAcc + material.highCostPerUnit * material.quantityHigh,
                0
              ),
          }),
          { costLow: 0, costHigh: 0 }
        );

        return {
          laborCostLow: Math.ceil(acc.laborCostLow + laborCosts.costLow),
          laborCostHigh: Math.ceil(acc.laborCostHigh + laborCosts.costHigh),
          materialCostLow: Math.ceil(acc.materialCostLow + materialCosts.costLow),
          materialCostHigh: Math.ceil(acc.materialCostHigh + materialCosts.costHigh),
          laborRateLow: Math.min(acc.laborRateLow || Infinity, laborCosts.rateLow),
          laborRateHigh: Math.max(acc.laborRateHigh || 0, laborCosts.rateHigh),
        };
      },
      {
        laborCostLow: 0,
        laborCostHigh: 0,
        materialCostLow: 0,
        materialCostHigh: 0,
        laborRateLow: Infinity,
        laborRateHigh: 0,
      }
    );
  };

  const {
    laborCostLow,
    laborCostHigh,
    materialCostLow,
    materialCostHigh,
    laborRateLow,
    laborRateHigh,
  } = calculateSubTaskCosts();

  const totalEstimateLow = laborCostLow + materialCostLow;
  const totalEstimateHigh = laborCostHigh + materialCostHigh;

  const fetchImagesForLineItem = async () => {
    try {
      const images = await Promise.all(
        lineItem.analysis.imageIds.map(async (imageId) => ({
          url: await propertyService.getPropertyImageUrl(propertyId, imageId),
          description: `Image ID: ${imageId}`,
        }))
      );
      setCarouselImages(images);
      setIsCarouselOpen(true);
    } catch (error) {
      console.error('Error fetching images for line item:', error);
    }
  };

  const hasImages = lineItem.analysis.imageIds && lineItem.analysis.imageIds.length > 0;

  return (
    <div className="overflow-hidden bg-white transition-all duration-200 border-b border-gray-100">
      {/* Header - always visible */}
      <div 
        className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={toggleExpanded}
      >
        <div className="flex items-center gap-3">
          <ChevronDown className={cn(
            "h-5 w-5 text-gray-500 transition-transform duration-200",
            isExpanded && "transform rotate-180"
          )} />
          <span className="font-medium text-gray-800">
            {lineItem.description}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {hasImages && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={(e) => {
                e.stopPropagation();
                fetchImagesForLineItem();
              }}
              className="flex items-center gap-1 h-7 px-2 text-xs py-0 border-gray-300"
            >
              <ExternalLink className="h-3 w-3" />
              <span>Images ({lineItem.analysis.imageIds.length})</span>
            </Button>
          )}
          <span className="text-sm font-semibold text-fxer-blue whitespace-nowrap">
            ${Math.ceil(totalEstimateLow).toLocaleString()} - ${Math.ceil(totalEstimateHigh).toLocaleString()}
          </span>
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-5 py-4 pl-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Labor Section */}
            <div className="bg-gray-50/50 rounded p-4">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                <User className="h-4 w-4 mr-2 text-fxer-blue" />
                Labor Details
              </h4>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm border-b border-gray-200 pb-2">
                  <span className="text-gray-600 flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-gray-500" />
                    Time Required
                  </span>
                  <span className="font-medium">
                    {Math.ceil(lineItem.analysis.subTasks.reduce((sum, subTask) => 
                      sum + subTask.options.reduce((optSum, option) => 
                        optSum + option.expectedLaborHoursLow, 0), 0))} - {Math.ceil(lineItem.analysis.subTasks.reduce((sum, subTask) => 
                          sum + subTask.options.reduce((optSum, option) => 
                            optSum + option.expectedLaborHoursHigh, 0), 0))} hours
                  </span>
                </div>
                
                <div className="flex justify-between items-center text-sm border-b border-gray-200 pb-2">
                  <span className="text-gray-600">Hourly Rate</span>
                  <span className="font-medium">${laborRateLow.toFixed(2)} - ${laborRateHigh.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center text-sm pt-1 font-semibold">
                  <span>Labor Total</span>
                  <span className="text-fxer-blue">${laborCostLow.toLocaleString()} - ${laborCostHigh.toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            {/* Materials Section */}
            <div className="bg-gray-50/50 rounded p-4">
              <div 
                className="font-semibold text-gray-700 mb-3 flex items-center justify-between cursor-pointer"
                onClick={toggleMaterials}
              >
                <div className="flex items-center">
                  <Wrench className="h-4 w-4 mr-2 text-fxer-blue" />
                  Materials
                </div>
                <div className="p-1 rounded-full hover:bg-gray-200 transition-colors">
                  {showMaterials ? 
                    <Minus className="h-3.5 w-3.5 text-gray-600" /> : 
                    <Plus className="h-3.5 w-3.5 text-gray-600" />
                  }
                </div>
              </div>
              
              {showMaterials ? (
                <div className="max-h-48 overflow-y-auto space-y-2 mb-3 pr-1">
                  {lineItem.analysis.subTasks.map((subTask, index) =>
                    subTask.options.flatMap((option, optIndex) =>
                      option.materials.map((material, matIndex) => (
                        <div 
                          key={`${index}-${optIndex}-${matIndex}`} 
                          className="bg-white p-2 rounded-md border border-gray-200"
                        >
                          <div className="font-medium text-sm">{material.description}</div>
                          <div className="text-xs text-gray-600 flex justify-between mt-1">
                            <span>${(material.lowCostPerUnit).toFixed(2)} - ${(material.highCostPerUnit).toFixed(2)}/unit</span>
                            <span>Qty: {material.quantityLow} - {material.quantityHigh}</span>
                          </div>
                        </div>
                      ))
                    )
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic mb-3">
                  Click to view {lineItem.analysis.subTasks.reduce((count, subTask) => count + subTask.options.reduce((c, option) => c + option.materials.length, 0), 0)} materials
                </div>
              )}
              
              <div className="flex justify-between items-center text-sm pt-1 font-semibold border-t border-gray-200">
                <span>Materials Total</span>
                <span className="text-fxer-blue">${materialCostLow.toLocaleString()} - ${materialCostHigh.toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          {/* Total Cost */}
          <div className="mt-5 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-700">Estimated Cost:</span>
              <span className="font-bold text-lg text-fxer-blue">
                ${Math.ceil(totalEstimateLow).toLocaleString()} - ${Math.ceil(totalEstimateHigh).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Image Dialog */}
      <Dialog open={isCarouselOpen} onOpenChange={setIsCarouselOpen}>
        <DialogContent className="max-w-5xl w-full p-0 bg-black text-white border-none max-h-[90vh] overflow-y-auto">
          <div className="p-4 bg-gray-900 border-b border-gray-800 flex items-center justify-between">
            <h3 className="text-lg font-medium">Images for {lineItem.description}</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsCarouselOpen(false)}
              className="text-gray-400 hover:text-white"
            >
              Close
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-6 bg-gray-950">
            {carouselImages.map((image, index) => (
              <div key={index} className="group relative overflow-hidden rounded-md border border-gray-800 aspect-square">
                <img
                  src={image.url}
                  alt={`Related image ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900/90 to-transparent p-3">
                  <div className="text-xs text-gray-300">
                    Image {index + 1} of {carouselImages.length}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InspectionQuoteLineItemDetails;